import React, { useEffect, useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Container, 
  Box, 
  Button,
  IconButton,
  Drawer,
  useTheme,
  useMediaQuery,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Tooltip
} from '@mui/material';
import { useAuth } from './AuthContext';
import { io } from 'socket.io-client';
import api from './axiosConfig';
import logo from './Salon Logic Square 2.gif';
import { CalendarMonth as BookingIcon } from '@mui/icons-material';
import { usePermissions } from './PermissionsContext';
import { getHomeRouteForRole } from './utils/homeRoute';

// Import icons
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import NotificationsIcon from '@mui/icons-material/Notifications';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import GridViewIcon from '@mui/icons-material/GridView';
import CalculateIcon from '@mui/icons-material/Calculate';
import InventoryIcon from '@mui/icons-material/Inventory';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const Layout = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);

  // "Hub" needs to point at this person's own hub, not always the Owner's —
  // same fix as the various "Back to Hub" buttons elsewhere in the app.
  const homeRoute = getHomeRouteForRole(user?.role);

  const mainMenuItems = [
    { 
      title: 'Hub', 
      path: homeRoute, 
      icon: <GridViewIcon />,
      description: 'Main dashboard & tools'
    },
    { 
      title: 'Calculator', 
      path: '/calculator', 
      icon: <CalculateIcon />,
      description: 'Service pricing',
      permission: 'calculator'
    },
    { 
      title: 'Stock', 
      path: '/stock/products', 
      icon: <InventoryIcon />,
      description: 'Inventory management',
      permission: 'stock'
    },
    { 
      title: 'Reports', 
      path: '/reports/dashboard', 
      icon: <AssessmentIcon />,
      description: 'Business analytics',
      permission: 'reports'
    },
    { 
      title: 'Team', 
      path: '/team', 
      icon: <PeopleIcon />,
      description: 'Staff management',
      roles: ['ACCOUNT_OWNER', 'ACCOUNT_MANAGER'],
      permission: 'team'
    },
    { 
      title: 'Time', 
      path: '/time', 
      icon: <AccessTimeIcon />,
      description: 'Holidays & absence',
      permission: 'time'
    },
    {
      title: 'Online Booking',
      path: '/pro/booking-settings',
      icon: <BookingIcon />,
      description: 'Booking settings & diary',
      permission: 'online_booking',
    },
  ];

  useEffect(() => {
    if (!isAuthenticated || !user) {
      navigate('/');
      return;
    }

    const socket = io('/', {
      path: '/socket.io',
      auth: {
        token: localStorage.getItem('access_token')
      }
    });

    socket.on('connect', () => {
      console.log('Connected to WebSocket');
    });

    socket.on('new_message', (message) => {
      setUnreadCount(prev => prev + 1);
      setNotifications(prev => [message, ...prev]);
    });

    api.get('/messages/unread-count')
      .then(response => setUnreadCount(response.data.count))
      .catch(error => console.error('Error fetching unread count:', error));

    return () => socket.disconnect();
  }, [isAuthenticated, user, navigate]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/');
    }
  };

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleNotificationClick = (event) => setAnchorEl(event.currentTarget);
  const handleNotificationClose = () => setAnchorEl(null);
  const handleUserMenuClick = (event) => setUserMenuAnchor(event.currentTarget);
  const handleUserMenuClose = () => setUserMenuAnchor(null);

  const getFilteredMenuItems = () => {
    return mainMenuItems.filter(item => {
      // Role check
      if (item.roles && !item.roles.includes(user?.role)) return false;
      // Permission check (owners always pass via hasPermission — is_owner=true)
      if (item.permission && !hasPermission(item.permission)) return false;
      return true;
    });
  };

  const getPageTitle = () => {
    if (location.pathname === '/dashboard') return 'Dashboard';
    if (location.pathname === '/calculator') return 'Pricing Calculator';
    if (location.pathname.startsWith('/calculator/import')) return 'Import Data';
    if (location.pathname.startsWith('/stock')) return 'Stock Management';
    if (location.pathname.startsWith('/reports')) return 'Reports';
    if (location.pathname === '/team') return 'Team Management';
    if (location.pathname === '/time') return 'Time Management';
    if (location.pathname.startsWith('/settings/hmrc')) return 'HMRC Settings';
    if (location.pathname.startsWith('/vat')) return 'VAT Returns';
    if (location.pathname.startsWith('/pro/booking-settings')) return 'Online Booking';
    return 'SalonLogic Pro';
  };

  const isSubPage = ![
    '/dashboard',
    '/calculator',
    '/stock/products',
    '/reports/dashboard',
    '/team',
    '/time',
    '/pro/booking-settings',
  ].includes(location.pathname);

  if (!isAuthenticated || !user) {
    return null;
  }

  const SidebarContent = () => (
    <Box 
      component="nav"
      aria-label="Main navigation"
      sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%',
        bgcolor: 'linear-gradient(180deg, #1976d2 0%, #1565c0 100%)'
      }}
    >
      {/* Logo */}
      <Box sx={{ 
        p: 3, 
        textAlign: 'center',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <img 
          src={logo} 
          alt="SalonLogic Pro - Go to dashboard" 
          style={{ 
            maxWidth: '100%', 
            height: 'auto',
            maxHeight: '80px',
            cursor: 'pointer'
          }}
          onClick={() => navigate(homeRoute)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              navigate(homeRoute);
            }
          }}
        />
      </Box>

      <List sx={{ flexGrow: 1, pt: 2 }} component="ul">
        {getFilteredMenuItems().map((item, index) => (
          <li key={index} style={{ listStyle: 'none' }}>
            <Tooltip 
              title={item.description}
              placement="right"
              arrow
            >
              <ListItem 
                button 
                component={Link} 
                to={item.path}
                onClick={() => isMobile && setMobileOpen(false)}
                selected={location.pathname.startsWith(item.path)}
                aria-label={item.description}
                aria-current={location.pathname.startsWith(item.path) ? 'page' : undefined}
                sx={{ 
                  mx: 1,
                  mb: 1,
                  borderRadius: '8px',
                  color: 'white',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255,255,255,0.15)',
                    '&:hover': {
                      bgcolor: 'rgba(255,255,255,0.2)',
                    }
                  },
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.1)',
                  }
                }}
              >
                <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  secondary={item.description}
                  secondaryTypographyProps={{
                    sx: { color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }
                  }}
                />
              </ListItem>
            </Tooltip>
          </li>
        ))}
      </List>

      <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)' }} />

      {/* User Profile Section */}
      <Box sx={{ p: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1.5,
            p: 1.5,
            bgcolor: 'rgba(255,255,255,0.1)',
            borderRadius: '8px',
            cursor: 'pointer',
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.15)',
            }
          }}
          onClick={handleUserMenuClick}
          role="button"
          tabIndex={0}
          aria-label="Open user menu"
          aria-haspopup="true"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleUserMenuClick(e);
            }
          }}
        >
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'white', color: '#1976d2' }}>
            {user?.email?.[0]?.toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white', 
                fontWeight: 600,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.email?.split('@')[0]}
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255,255,255,0.7)',
                textTransform: 'capitalize'
              }}
            >
              {user?.role?.replace('_', ' ').toLowerCase()}
            </Typography>
          </Box>
          <SettingsIcon sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 20 }} aria-hidden="true" />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
      <a 
        href="#main-content" 
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
        onFocus={(e) => {
          e.target.style.position = 'fixed';
          e.target.style.left = '10px';
          e.target.style.top = '10px';
          e.target.style.width = 'auto';
          e.target.style.height = 'auto';
          e.target.style.overflow = 'visible';
          e.target.style.zIndex = '9999';
          e.target.style.padding = '10px 20px';
          e.target.style.background = '#1976d2';
          e.target.style.color = 'white';
          e.target.style.textDecoration = 'none';
          e.target.style.borderRadius = '4px';
        }}
        onBlur={(e) => {
          e.target.style.position = 'absolute';
          e.target.style.left = '-9999px';
          e.target.style.width = '1px';
          e.target.style.height = '1px';
          e.target.style.overflow = 'hidden';
        }}
      >
        Skip to main content
      </a>

      <AppBar 
        position="static" 
        elevation={0}
        component="header"
        sx={{ 
          bgcolor: 'white',
          color: '#1976d2',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
              aria-label="Open navigation menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation"
            >
              <MenuIcon />
            </IconButton>
          )}

          {isSubPage && (
            <IconButton
              color="inherit"
              onClick={() => navigate(-1)}
              sx={{ mr: 1 }}
              aria-label="Go back to previous page"
            >
              <ArrowBackIcon />
            </IconButton>
          )}

          <Typography 
            variant="h6" 
            component="h1" 
            sx={{ 
              flexGrow: 1,
              fontWeight: 600,
              color: '#2c3e50'
            }}
          >
            {getPageTitle()}
          </Typography>

          <IconButton
            color="inherit"
            component={Link}
            to="/messages"
            sx={{ mr: 1 }}
            aria-label={`Messages${unreadCount > 0 ? `, ${unreadCount} unread` : ''}`}
          >
            <Badge badgeContent={unreadCount} color="error">
              <EmailIcon />
            </Badge>
          </IconButton>

          <IconButton
            color="inherit"
            onClick={handleNotificationClick}
            aria-label={`Notifications${notifications.length > 0 ? `, ${notifications.length} new` : ''}`}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
          >
            <Badge badgeContent={notifications.length} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>

          {!isMobile && (
            <IconButton
              onClick={handleUserMenuClick}
              sx={{ ml: 2 }}
              aria-label="Open user account menu"
              aria-haspopup="true"
              aria-expanded={Boolean(userMenuAnchor)}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>
                {user?.email?.[0]?.toUpperCase()}
              </Avatar>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleNotificationClose}
        PaperProps={{
          sx: {
            maxHeight: '400px',
            width: '350px',
            mt: 1
          }
        }}
        aria-label="Notifications"
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="h6" component="h2">Notifications</Typography>
        </Box>
        {notifications.length > 0 ? (
          notifications.map((notification, index) => (
            <MenuItem 
              key={index}
              onClick={() => {
                handleNotificationClose();
                navigate(`/messages/${notification.id}`);
              }}
              sx={{
                borderBottom: '1px solid #f5f5f5',
                whiteSpace: 'normal',
                py: 2
              }}
            >
              <Box>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: notification.status === 'UNREAD' ? 'bold' : 'normal'
                  }}
                >
                  {notification.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mt: 0.5
                  }}
                >
                  {notification.content}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  {new Date(notification.created_at).toLocaleString()}
                </Typography>
              </Box>
            </MenuItem>
          ))
        ) : (
          <MenuItem disabled>
            <Typography color="text.secondary">No new notifications</Typography>
          </MenuItem>
        )}
        {notifications.length > 0 && (
          <MenuItem 
            onClick={() => {
              handleNotificationClose();
              navigate('/messages');
            }}
            sx={{ 
              justifyContent: 'center', 
              color: 'primary.main',
              borderTop: '1px solid #e0e0e0',
              fontWeight: 600
            }}
          >
            View All Messages
          </MenuItem>
        )}
      </Menu>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={handleUserMenuClose}
        PaperProps={{
          sx: { width: '200px', mt: 1 }
        }}
        aria-label="User account menu"
      >
        <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {user?.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.role}
          </Typography>
        </Box>
        <MenuItem 
          onClick={() => {
            handleUserMenuClose();
            navigate('/settings');
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        <Drawer
          variant="temporary"
          anchor="left"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 280 },
          }}
          id="mobile-navigation"
        >
          <SidebarContent />
        </Drawer>

        <Box sx={{ 
          width: 280,
          display: { xs: 'none', md: 'block' },
          background: 'linear-gradient(180deg, #1976d2 0%, #1565c0 100%)',
          borderRight: '1px solid #e0e0e0'
        }}>
          <SidebarContent />
        </Box>

        <Box 
          component="main"
          id="main-content"
          tabIndex={-1}
          sx={{ 
            flexGrow: 1,
            overflow: 'auto',
            bgcolor: '#f5f5f5'
          }}
        >
          <Container maxWidth="xl" sx={{ py: 3 }}>
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;